import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  InputAdornment,
  IconButton,
  useTheme,
  useMediaQuery,
  Grid
} from '@mui/material';
import { 
  Visibility, 
  VisibilityOff, 
  Email, 
  Lock 
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { THEME_COLORS, GRADIENTS, SHADOWS, BORDER_RADIUS } from '../../theme/themeConfig';
import ToastNotification from '../pages/common/Toastnotification';
import loginImage from '../../rawAssets/login.png';

interface ToastState {
  open: boolean;
  message: string;
  severity: 'success' | 'error' | 'warning' | 'info';
}

const Login: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  
  const [toast, setToast] = useState<ToastState>({
    open: false,
    message: '',
    severity: 'info',
  });
  
  const { login } = useAuth();
  const navigate = useNavigate();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const showToast = (message: string, severity: 'success' | 'error' | 'warning' | 'info') => {
    setToast({ open: true, message, severity });
  };

  const handleCloseToast = () => {
    setToast({ ...toast, open: false });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password) {
      setError('Please fill in all fields');
      showToast('Please fill in all fields', 'error');
      return;
    }
    try {
      setError('');
      setLoading(true);
      await login(email, password);
      showToast('Login successful! Redirecting...', 'success');
      setTimeout(() => { navigate('/admin/dashboard'); }, 1000);
    } catch (error: any) {
      const errorMessage = 'Failed to sign in: ' + error.message;
      setError(errorMessage);
      showToast(errorMessage, 'error');
    } finally {
      setLoading(false);
    }
  };

  const GoogleIcon = () => (
    <svg width="18" height="18" viewBox="0 0 24 24">
      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
    </svg>
  );

  const linkColor = isDark
    ? theme.palette.primary.dark
    : theme.palette.primary.main;

  const linkStyle: React.CSSProperties = {
    color: linkColor,
    textDecoration: 'none',
    fontWeight: 700,
  };

  // ── Decorative SVG accent shapes ─────────────────────────────────────────
  const AccentShapes = () => (
    <Box
      sx={{
        position: 'absolute',
        inset: 0,
        pointerEvents: 'none',
        overflow: 'hidden',
        zIndex: 0,
      }}
    >
      {/* Top-left large ring */}
      <Box
        sx={{
          position: 'absolute',
          top: -80,
          left: -80,
          width: 260,
          height: 260,
          borderRadius: '50%',
          border: `2px solid ${
            isDark
              ? 'rgba(255,255,255,0.06)'
              : `${theme.palette.primary.main}18`
          }`,
        }}
      />
      {/* Top-left small ring */}
      <Box
        sx={{
          position: 'absolute',
          top: -20,
          left: -20,
          width: 140,
          height: 140,
          borderRadius: '50%',
          border: `1.5px solid ${
            isDark
              ? 'rgba(255,255,255,0.04)'
              : `${theme.palette.primary.main}22`
          }`,
        }}
      />
      {/* Bottom-right blob */}
      <Box
        sx={{
          position: 'absolute',
          bottom: -60,
          right: -60,
          width: 220,
          height: 220,
          borderRadius: '50%',
          background: isDark
            ? 'radial-gradient(circle, rgba(255,255,255,0.03) 0%, transparent 70%)'
            : `radial-gradient(circle, ${theme.palette.primary.main}0d 0%, transparent 70%)`,
        }}
      />
      {/* Tiny dot cluster top-right */}
      {[0, 1, 2].map((col) =>
        [0, 1, 2].map((row) => (
          <Box
            key={`dot-${col}-${row}`}
            sx={{
              position: 'absolute',
              top: 32 + row * 14,
              right: 40 + col * 14,
              width: 4,
              height: 4,
              borderRadius: '50%',
              backgroundColor: isDark
                ? 'rgba(255,255,255,0.08)'
                : `${theme.palette.primary.main}33`,
            }}
          />
        ))
      )}
      {/* Bottom-left horizontal line accent */}
      <Box
        sx={{
          position: 'absolute',
          bottom: 52,
          left: 32,
          width: 56,
          height: 2,
          borderRadius: 1,
          background: isDark
            ? 'rgba(255,255,255,0.07)'
            : `${theme.palette.primary.main}30`,
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          bottom: 60,
          left: 32,
          width: 32,
          height: 2,
          borderRadius: 1,
          background: isDark
            ? 'rgba(255,255,255,0.04)'
            : `${theme.palette.primary.main}1a`,
        }}
      />
    </Box>
  );

  // ── Mobile layout ─────────────────────────────────────────────────────────
  if (isMobile) {
    return (
      <>
        <Container
          component="main"
          maxWidth="sm"
          sx={{
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            py: 2,
            backgroundColor: 'background.default',
          }}
        >
          <Paper
            elevation={0}
            sx={{
              p: 4,
              width: '100%',
              borderRadius: BORDER_RADIUS.lg,
              backgroundColor: 'background.paper',
              boxShadow: isDark ? '0 8px 32px rgba(0,0,0,0.5)' : SHADOWS.lg,
              border: isDark ? `1px solid ${theme.palette.divider}` : 'none',
            }}
          >
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <Typography
                component="h1"
                variant="h4"
                sx={{ mb: 4, fontWeight: 'bold', textAlign: 'center', color: 'text.primary' }}
              >
                Welcome Back
              </Typography>

              <Box component="form" onSubmit={handleSubmit} sx={{ width: '100%' }}>
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  id="email"
                  label="Email"
                  name="email"
                  autoComplete="email"
                  autoFocus
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  sx={{ mb: 2 }}
                />

                <TextField
                  margin="normal"
                  required
                  fullWidth
                  name="password"
                  label="Password"
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={() => setShowPassword(!showPassword)}
                          edge="end"
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={{ mb: 1 }}
                />

                <Box sx={{ textAlign: 'right', mb: 2 }}>
                  <Link to="/admin/forgot-password" style={{ ...linkStyle, fontSize: '0.875rem' }}>
                    Forgot Password?
                  </Link>
                </Box>

                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  size="large"
                  disabled={loading}
                  sx={{ mt: 2, mb: 2, py: 1.5, textTransform: 'none', fontSize: '1rem', fontWeight: 'bold' }}
                >
                  {loading ? 'Signing In...' : 'Sign In'}
                </Button>

                <Box sx={{ textAlign: 'center', mt: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    Don't have an account?{' '}
                    <Link to="/admin/register" style={linkStyle}>
                      Register here
                    </Link>
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Paper>
        </Container>

        <ToastNotification
          open={toast.open}
          message={toast.message}
          severity={toast.severity}
          onClose={handleCloseToast}
        />
      </>
    );
  }

  // ── Desktop layout ────────────────────────────────────────────────────────
  return (
    <>
      <Box sx={{ minHeight: '100vh', display: 'flex', backgroundColor: 'background.default' }}>
        <Grid container sx={{ minHeight: '100vh' }}>

          {/* ── Left side — Form panel ─────────────────────────────────── */}
          <Grid
            item
            xs={12}
            md={6}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: 'background.default',
              padding: 4,
              position: 'relative',
              overflow: 'hidden',
            }}
          >
            {/* Decorative background shapes */}
            <AccentShapes />

            <Box
              sx={{
                width: '100%',
                maxWidth: 400,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                position: 'relative',
                zIndex: 1,
              }}
            >

              {/* ── Logo mark ─────────────────────────────── */}
              <Box
                sx={{
                  mb: 5,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                }}
              >
                {/* Geometric logo icon */}
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: '10px',
                    background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: `0 4px 14px ${theme.palette.primary.main}55`,
                    flexShrink: 0,
                  }}
                >
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                    <path
                      d="M10 2L17.32 6.5V15.5L10 20L2.68 15.5V6.5L10 2Z"
                      fill="white"
                      fillOpacity="0.9"
                    />
                    <path
                      d="M10 5.5L14.33 8V13L10 15.5L5.67 13V8L10 5.5Z"
                      fill="white"
                      fillOpacity="0.3"
                    />
                  </svg>
                </Box>
                <Typography
                  sx={{
                    fontWeight: 800,
                    fontSize: '1.1rem',
                    letterSpacing: '-0.02em',
                    color: 'text.primary',
                    lineHeight: 1,
                  }}
                >
                  Admin Portal
                </Typography>
              </Box>

              {/* ── Heading block ─────────────────────────── */}
              <Box sx={{ mb: 4, width: '100%' }}>
                <Typography
                  component="h1"
                  sx={{
                    fontWeight: 800,
                    fontSize: '2rem',
                    letterSpacing: '-0.03em',
                    lineHeight: 1.15,
                    color: 'text.primary',
                    mb: 0.75,
                  }}
                >
                  Welcome back
                </Typography>
                {/* Animated underline accent */}
                <Box
                  sx={{
                    height: 3,
                    width: 48,
                    borderRadius: 2,
                    background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.primary.light})`,
                    mb: 1.5,
                  }}
                />
                <Typography
                  variant="body2"
                  sx={{
                    color: 'text.secondary',
                    fontSize: '0.9rem',
                    lineHeight: 1.6,
                  }}
                >
                  Sign in to your account to continue
                </Typography>
              </Box>

              {/* ── Form ──────────────────────────────────── */}
              <Box component="form" onSubmit={handleSubmit} sx={{ width: '100%' }}>
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  id="email"
                  placeholder="Email"
                  name="email"
                  autoComplete="email"
                  autoFocus
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  variant="outlined"
                  sx={{ mb: 2, '& .MuiInputLabel-root': { display: 'none' } }}
                />

                <TextField
                  margin="normal"
                  required
                  fullWidth
                  name="password"
                  placeholder="Password"
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={() => setShowPassword(!showPassword)}
                          edge="end"
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={{ mb: 1, '& .MuiInputLabel-root': { display: 'none' } }}
                />

                <Box sx={{ textAlign: 'right', mb: 2 }}>
                  <Link to="/admin/forgot-password" style={{ ...linkStyle, fontSize: '0.875rem' }}>
                    Forgot Password?
                  </Link>
                </Box>

                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  size="large"
                  disabled={loading}
                  sx={{ mt: 2, mb: 2, py: 1.5, textTransform: 'none', fontSize: '1rem', fontWeight: 'bold' }}
                >
                  {loading ? 'Signing In...' : 'Sign In'}
                </Button>

                {/* <Button
                  fullWidth
                  variant="outlined"
                  size="large"
                  sx={{ mb: 3, py: 1.5, textTransform: 'none', fontSize: '1rem', fontWeight: 'normal' }}
                >
                  <Box component="span" sx={{ mr: 1, display: 'flex', alignItems: 'center' }}>
                    <GoogleIcon />
                  </Box>
                  Continue with Google
                </Button> */}

                <Box sx={{ textAlign: 'center', mt: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    Don't have an account?{' '}
                    <Link to="/admin/register" style={linkStyle}>
                      Register here
                    </Link>
                  </Typography>
                </Box>
              </Box>

              {/* ── Footer note ───────────────────────────── */}
              <Box
                sx={{
                  mt: 6,
                  pt: 3,
                  borderTop: `1px solid ${theme.palette.divider}`,
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                }}
              >
                <Box
                  sx={{
                    width: 6,
                    height: 6,
                    borderRadius: '50%',
                    backgroundColor: theme.palette.success.main,
                    flexShrink: 0,
                  }}
                />
                <Typography
                  variant="caption"
                  sx={{ color: 'text.disabled', fontSize: '0.75rem' }}
                >
                  Secured with end-to-end encryption
                </Typography>
              </Box>
            </Box>
          </Grid>

          {/* ── Right side — Image panel ───────────────────────────────── */}
          <Grid
            item
            xs={12}
            md={6}
            sx={{
              backgroundImage: `url(${loginImage})`,
              backgroundRepeat: 'no-repeat',
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              position: 'relative',
              overflow: 'hidden',
            }}
          >
            {/* Multi-layer overlay for depth */}
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                background: isDark
                  ? 'linear-gradient(160deg, rgba(0,0,0,0.25) 0%, rgba(0,0,0,0.65) 100%)'
                  : 'linear-gradient(160deg, rgba(0,0,0,0.08) 0%, rgba(0,0,0,0.52) 100%)',
              }}
            />

            {/* Primary colour tint strip — left edge */}
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: 5,
                height: '100%',
                background: `linear-gradient(180deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                opacity: 0.85,
              }}
            />

            {/* Top-right decorative circle */}
            <Box
              sx={{
                position: 'absolute',
                top: -60,
                right: -60,
                width: 200,
                height: 200,
                borderRadius: '50%',
                border: '1.5px solid rgba(255,255,255,0.12)',
                pointerEvents: 'none',
              }}
            />
            <Box
              sx={{
                position: 'absolute',
                top: -10,
                right: -10,
                width: 110,
                height: 110,
                borderRadius: '50%',
                border: '1px solid rgba(255,255,255,0.08)',
                pointerEvents: 'none',
              }}
            />
          </Grid>

        </Grid>
      </Box>

      <ToastNotification
        open={toast.open}
        message={toast.message}
        severity={toast.severity}
        onClose={handleCloseToast}
      />
    </>
  );
};

export default Login;